import { PlacementRuleType } from '../../../api/generated';
import type { EditorNode } from './types';

export interface Chain {
  id: string;
  items: EditorNode[];
}

export interface Layout {
  chains: Chain[];
  free: EditorNode[];
}

export const isAfterRule = (type: PlacementRuleType): boolean =>
  type === PlacementRuleType.After || type === PlacementRuleType.ImmediatelyAfter;

export const isBeforeRule = (type: PlacementRuleType): boolean =>
  type === PlacementRuleType.Before || type === PlacementRuleType.ImmediatelyBefore;

export interface GraphRelations {
  getPredId: (wi: EditorNode) => string | undefined;
  getSuccessor: (wiId: string) => EditorNode | undefined;
  getSuccessors: (wiId: string) => EditorNode[];
}

// Builds the predecessor/successor lookups shared by the layout (which walks
// them into chains) and the branch-connector geometry (which draws the
// cross-row links between a wrapped node and its predecessor/successor).
export function createRelations(nodes: EditorNode[]): GraphRelations {
  const byId = new Map(nodes.map((wi) => [wi.id, wi]));

  // X has predecessor P if either: X.deps has AFTER:P, or P.deps has BEFORE:X.
  // Treating both directions symmetrically lets chains form whether the API
  // stores rules as AFTER on the successor or as BEFORE on the predecessor.
  const getPredId = (wi: EditorNode): string | undefined => {
    const fromOwn = wi.dependencies.find((d) => isAfterRule(d.type) && d.targetId)?.targetId;
    if (fromOwn) return fromOwn;
    for (const other of nodes) {
      if (other.id === wi.id) continue;
      const matches = other.dependencies.some(
        (d) => isBeforeRule(d.type) && d.targetId === wi.id
      );
      if (matches) return other.id;
    }
    return undefined;
  };

  // All successors of X: every S where X.deps has BEFORE:S, or S.deps has
  // AFTER:X. Before-targets come first (an explicit forward link), then the
  // nodes that declare themselves after X. A node may have several successors
  // (a fork); the layout decides which one continues the row inline.
  const getSuccessors = (wiId: string): EditorNode[] => {
    const wi = byId.get(wiId);
    if (!wi) return [];
    const result: EditorNode[] = [];
    const seen = new Set<string>();
    const add = (node: EditorNode | undefined): void => {
      if (node && !seen.has(node.id)) {
        seen.add(node.id);
        result.push(node);
      }
    };
    for (const d of wi.dependencies) {
      if (isBeforeRule(d.type) && d.targetId) add(byId.get(d.targetId));
    }
    for (const n of nodes) {
      if (n.dependencies.some((d) => isAfterRule(d.type) && d.targetId === wiId)) add(n);
    }
    return result;
  };

  const getSuccessor = (wiId: string): EditorNode | undefined => getSuccessors(wiId)[0];

  return { getPredId, getSuccessor, getSuccessors };
}

export const hasFirstRule = (wi: EditorNode): boolean =>
  wi.dependencies.some((d) => d.type === PlacementRuleType.First);

export const hasLastRule = (wi: EditorNode): boolean =>
  wi.dependencies.some((d) => d.type === PlacementRuleType.Last);

// A chain is anchored when one of its items carries a first/last placement rule.
export const isAnchoredChain = (chain: Chain): boolean =>
  chain.items.some((wi) => hasFirstRule(wi) || hasLastRule(wi));

// Every node that takes part in a dependency: it has rules of its own, or it
// is the target of another node's rule.
function collectLinkedIds(nodes: EditorNode[], byId: Map<string, EditorNode>): Set<string> {
  const linkedIds = new Set<string>();
  for (const wi of nodes) {
    if (wi.dependencies.length > 0) linkedIds.add(wi.id);
    for (const dep of wi.dependencies) {
      if (dep.targetId && byId.has(dep.targetId)) linkedIds.add(dep.targetId);
    }
  }
  return linkedIds;
}

// Which node, if any, explicitly "before"-links to a given node. That owner
// should keep the node as its own inline continuation, so a different node
// that merely shares it as a successor yields and branches instead.
function buildBeforeLinkOwners(nodes: EditorNode[]): Map<string, string> {
  const beforeLinkOwner = new Map<string, string>();
  for (const wi of nodes) {
    for (const d of wi.dependencies) {
      if (isBeforeRule(d.type) && d.targetId) beforeLinkOwner.set(d.targetId, wi.id);
    }
  }
  return beforeLinkOwner;
}

// The successor that continues the row inline after the current node.
// A node linked "immediately after" the current one must stay adjacent, so
// it always wins the inline continuation over plain "after" successors.
// Otherwise prefer a successor that no other node "before"-links to, so the
// choice is independent of walk order.
function nextInlineSuccessor(
  currentId: string,
  inChain: Set<string>,
  getSuccessors: (wiId: string) => EditorNode[],
  beforeLinkOwner: Map<string, string>
): EditorNode | undefined {
  const candidates: EditorNode[] = getSuccessors(currentId).filter((n) => !inChain.has(n.id));
  const isImmediatelyAfterCurrent = (n: EditorNode): boolean =>
    n.dependencies.some(
      (d) => d.type === PlacementRuleType.ImmediatelyAfter && d.targetId === currentId
    );
  const isFreeToFollow = (n: EditorNode): boolean => {
    const owner = beforeLinkOwner.get(n.id);
    return !owner || owner === currentId;
  };
  return (
    candidates.find((n) => isImmediatelyAfterCurrent(n) && isFreeToFollow(n)) ??
    candidates.find(isImmediatelyAfterCurrent) ??
    candidates.find(isFreeToFollow) ??
    candidates[0]
  );
}

// Walk a single row from its head, following successors. At each step we
// continue into a successor that hasn't already been placed in another row,
// so when a node forks (several items "after" it) one continues the row
// inline and the others stay free to become their own rows + link lines.
// Checking inChain also makes the walk cycle-safe.
function walkChain(
  head: EditorNode,
  inChain: Set<string>,
  getSuccessors: (wiId: string) => EditorNode[],
  beforeLinkOwner: Map<string, string>
): EditorNode[] {
  const chain: EditorNode[] = [];
  let current: EditorNode | undefined = head;
  while (current && !inChain.has(current.id)) {
    chain.push(current);
    inChain.add(current.id);
    current = nextInlineSuccessor(current.id, inChain, getSuccessors, beforeLinkOwner);
  }
  return chain;
}

// Append "last" nodes to the end of the primary chain (the one anchored by a
// "first" node, else the longest), so they sit at the end of the flow and
// pick up the inline connector. With no other chain to join, they stand alone.
function appendLastNodes(lastNodes: EditorNode[], chains: Chain[], inChain: Set<string>): void {
  if (lastNodes.length === 0) return;
  let primary = chains.find((chain) => chain.items.some(hasFirstRule));
  if (!primary) {
    for (const chain of chains) {
      if (!primary || chain.items.length > primary.items.length) primary = chain;
    }
  }
  if (primary) {
    primary.items.push(...lastNodes);
  } else {
    chains.push({ id: `chain-${chains.length}`, items: lastNodes });
  }
  lastNodes.forEach((wi) => inChain.add(wi.id));
}

export function computeLayout(nodes: EditorNode[]): Layout {
  const byId = new Map(nodes.map((wi) => [wi.id, wi]));
  const linkedIds = collectLinkedIds(nodes, byId);

  const { getPredId, getSuccessors } = createRelations(nodes);

  // "Last" nodes anchor the end of the flow, so they're held back and appended
  // to the primary chain after the walk instead of starting their own row.
  const roots = nodes.filter(
    (wi) => linkedIds.has(wi.id) && !getPredId(wi) && !hasLastRule(wi)
  );

  const beforeLinkOwner = buildBeforeLinkOwners(nodes);

  const inChain = new Set<string>();
  const chains: Chain[] = [];

  const pushChain = (head: EditorNode): void => {
    const chain = walkChain(head, inChain, getSuccessors, beforeLinkOwner);
    if (chain.length > 0) chains.push({ id: `chain-${chains.length}`, items: chain });
  };

  for (const root of roots) pushChain(root);

  // Place the remaining linked nodes (parallel branches whose head wasn't a
  // root, e.g. a second item that is "after" the same node as the main chain).
  let remaining = nodes.filter(
    (wi) => linkedIds.has(wi.id) && !inChain.has(wi.id) && !hasLastRule(wi)
  );
  while (remaining.length > 0) {
    // A head has no predecessor, or one already placed in another chain.
    // Fall back to any remaining node so a dependency cycle can't loop forever.
    const head =
      remaining.find((wi) => {
        const predId = getPredId(wi);
        return !predId || inChain.has(predId);
      }) ?? remaining[0];

    pushChain(head);
    remaining = remaining.filter((wi) => !inChain.has(wi.id));
  }

  const lastNodes = nodes.filter(
    (wi) => linkedIds.has(wi.id) && hasLastRule(wi) && !inChain.has(wi.id)
  );
  appendLastNodes(lastNodes, chains, inChain);

  return { chains, free: nodes.filter((wi) => !inChain.has(wi.id)) };
}
